<template>
    <div class="content-wrapper">
        <div class="row align-items-center">
            <div class="col-md-12 col-lg-6">
                <div class="d-flex align-items-center">
                    <nav aria-label="breadcrumb">
                        <ol class="breadcrumb p-0 d-flex align-items-center mb-primary">
                            <li class="breadcrumb-item page-header d-flex align-items-center">
                                <h4 class="mb-0">{{ $t('questions.question_details') }}</h4>
                            </li>
                            <template v-if="this.$can('view_questions')">
                                <li class="ml-2">|</li>
                                <li>
                                    <a :href="urlGenerator(courseWebUrl)"
                                       class="btn btn-link text-primary pl-2">{{
                                            $t('lessons.back_to_all_courses')
                                        }}</a>
                                </li>
                            </template>
                        </ol>
                    </nav>
                </div>
            </div>
        </div>

        <div class="lesson-profile mb-primary">
            <div class="card card-with-shadow py-5 border-0" style="min-height: 220px;">
                <app-overlay-loader v-if="loader"/>

                <div v-else class="row">
                    <!-- Left Column - Questions Details -->
                    <div class="col-12 col-sm-12 col-md-6 col-lg-6 col-xl-5 border-right">
                        <div class="media px-5 question-header-media align-items-center mb-4">
                            <div class="media-body question-info-header">
                                <p class="text-muted mb-0">{{ $t('questions.question_text') }}</p>
                                <p class="mb-0">{{ question.question_text }}</p>
                            </div>
                        </div>

                        <!-- Question Type Section -->
                        <div class="media mb-4 px-5">
                            <div class="media-body">
                                <p class="text-muted mb-0">{{ $t('questions.question_type') }}</p>
                                <p class="mb-0" v-if="question.question_type">{{ question.question_type }}</p>
                                <p class="mb-0" v-else>{{ $t('not_added_yet') }}</p>
                            </div>
                        </div>
                    </div>

                    <!-- Right Column - Dates & Additional Info -->
                    <div class="col-12 col-sm-12 col-md-6 col-lg-6 col-xl-7">
                        <div class="question-details px-5 mt-4 mt-sm-5 mt-md-0 mt-lg-0 mt-xl-0">
                            <div class="row">
                                <!-- Created and Updated Date Sections -->
                                <div class="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-6">
                                    <div class="custom">
                                        <!-- Created By -->
                                        <div class="media mb-4 ">
                                            <div class="align-self-center mr-3">
                                                <app-icon name="user"/>
                                            </div>
                                            <div class="media-body">
                                                <p class="text-muted mb-0">{{ $t('created_by') }}</p>
                                                <p class="mb-0" v-if="question.creator?.full_name">
                                                    {{ question.creator.full_name }}</p>
                                                <p class="mb-0" v-else>{{ $t('not_added_yet') }}</p>
                                            </div>
                                        </div>
                                        <!-- Created Date -->
                                        <div class="media mb-4 ">
                                            <div class="align-self-center mr-3">
                                                <app-icon name="calendar"/>
                                            </div>
                                            <div class="media-body">
                                                <p class="text-muted mb-0">{{ $t('created_at') }}</p>
                                                <p class="mb-0" v-if="question.created_at">
                                                    {{ formatDateTimeToLocalDate(question.created_at) }}</p>
                                                <p class="mb-0" v-else>{{ $t('not_added_yet') }}</p>
                                            </div>
                                        </div>

                                        <!-- Updated Date -->
                                        <div class="media mb-4">
                                            <div class="align-self-center mr-3">
                                                <app-icon name="calendar"/>
                                            </div>
                                            <div class="media-body">
                                                <p class="text-muted mb-0">{{ $t('updated_at') }}</p>
                                                <p class="mb-0" v-if="question.updated_at">
                                                    {{ formatDateTimeToLocalDate(question.updated_at) }}</p>
                                                <p class="mb-0" v-else>{{ $t('not_added_yet') }}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Answer Options Card -->
        <div class="card card-with-shadow py-5 border-0 mt-4" v-if="question.question_type">
            <div class="row">
                <!-- Left Column - Answer Options -->
                <div class="col-12 col-sm-12 col-md-6 col-lg-6 col-xl-5 border-right">
                    <div class="media px-5 question-header-media align-items-center mb-4">
                        <div class="media-body question-info-header">
                            <p class="text-muted mb-0">{{ $t('questions.answer_options') }}</p>

                            <!-- MCQ -->
                            <div v-if="question.question_type === 'mcq'">
                                <p class="mb-0" v-for="(option, index) in question.answers" :key="option.id">
                                    <span class="font-weight-bold">Option {{ index + 1 }}:</span> {{
                                        option.answer_text
                                    }}
                                </p>
                            </div>

                            <!-- Text -->
                            <div v-else-if="question.question_type === 'text'">
                                <p class="mb-0">
                                    <span class="font-weight-bold">{{ $t('Answer') }}:</span>
                                    {{ question.answers[0]?.answer_text || $t('not_added_yet') }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Right Column - (Optional for future) -->
                <div class="col-12 col-sm-12 col-md-6 col-lg-6 col-xl-7">
                    <!-- You can add analytics, explanation, or attachments here -->
                </div>
            </div>
        </div>

    </div>
</template>

<script>
import {urlGenerator} from '../../../../../common/Helper/AxiosHelper';
import {FormMixin} from "../../../../../core/mixins/form/FormMixin";
import {formatDateTimeToLocalDate} from "../../../../../common/Helper/Support/DateTimeHelper";
import {COURSEWEB} from "../../../../Config/ApiUrl";
import {mapState} from "vuex";
import QuestionCreateEditModal from "./QuestionCreateEditModal.vue";


export default {
    name: "QuestionDetails",
    mixins: [FormMixin],
    components: {QuestionCreateEditModal},
    props: {
        questionId: {},
    },
    data() {
        return {
            question: {},
            courseWebUrl: COURSEWEB,
            urlGenerator,
            formatDateTimeToLocalDate,
            promptAction: '',
            modalClass: '',
            promptIcon: '',
            promptSubtitle: '',
        };
    },
    mounted() {
        // Fetch question details on component mount
        this.$store.dispatch('getQuestionDetails', this.questionId);
        this.$store.dispatch('getUserInformation');
    },
    methods: {},
    computed: {
        ...mapState({
            questionDetails: state => state.question.question,
        }),
    },
    watch: {
        questionDetails: {
            handler: function (question) {
                this.question = question;

                // Check if question.title exists
                if (question.question_text) {
                    // Ensure document.title is a string and exists
                    if (typeof document.question_text === 'string' && document.question_text) {
                        let question_text = document.title.split('-');
                        question_text[0] = question.question_text;
                        document.question_text = question_text.join(' - ');
                    } else {
                        // If document.title is undefined or not a string, initialize it properly
                        document.question_text = question.question_text;
                    }
                }
            },
            deep: true
        }
    }
};
</script>
<style scoped>

</style>
